<script setup lang="ts">
  defineOptions({ name: "AppFormField" })

  withDefaults(
    defineProps<{
      /** Field label shown above the input. */
      label?: string
      /** Name used for aria association (links label → input via `for`/`id`). */
      name?: string
      /**
       * Error message. When truthy, shown below the field in red
       * and the label turns red.
       */
      error?: string | boolean
      /** Hint text shown below the field (replaced by error when error is set). */
      hint?: string
      /**
       * Size preset — affects font size and spacing.
       * Matches UFormField size values.
       */
      size?: "sm" | "md" | "lg" | "xl"
    }>(),
    {
      label: undefined,
      name: undefined,
      error: undefined,
      hint: undefined,
      size: "md",
    },
  )
</script>

<template>
  <div
    class="app-form-field"
    :class="[`app-form-field--${size}`, { 'app-form-field--error': error }]"
  >
    <label v-if="label" :for="name" class="app-form-field__label">
      {{ label }}
    </label>
    <div class="app-form-field__control">
      <slot />
    </div>
    <p
      v-if="error && typeof error === 'string'"
      class="app-form-field__message app-form-field__message--error"
    >
      {{ error }}
    </p>
    <p
      v-else-if="hint"
      class="app-form-field__message app-form-field__message--hint"
    >
      {{ hint }}
    </p>
  </div>
</template>

<style scoped>
  .app-form-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  /* Size variants */
  .app-form-field--sm {
    font-size: 0.75rem;
  }
  .app-form-field--md {
    font-size: 0.875rem;
  }
  .app-form-field--lg {
    font-size: 1rem;
  }
  .app-form-field--xl {
    font-size: 1.125rem;
  }

  .app-form-field__label {
    font-size: 0.875em;
    font-weight: 500;
    color: var(--app-form-field-label-color, rgba(255, 255, 255, 0.75));
    line-height: 1.25;
  }

  .app-form-field--error .app-form-field__label {
    color: var(--app-form-field-error-color, #f87171);
  }

  .app-form-field__control {
    display: flex;
    flex-direction: column;
  }

  .app-form-field__message {
    font-size: 0.75em;
    margin: 0;
    line-height: 1.4;
  }

  .app-form-field__message--error {
    color: var(--app-form-field-error-color, #f87171);
  }

  .app-form-field__message--hint {
    color: var(--app-form-field-hint-color, rgba(255, 255, 255, 0.5));
  }
</style>
